import { supabase } from '../lib/supabaseClient';
import { markdownToPlainText } from './markdown';

// Data access for news posts (public.posts, migration 20261023120000).
//
// Public reads go through RLS that only exposes published posts whose
// published_at has passed, so the public helpers don't filter on status
// themselves — but they still do, so an admin browsing the public site sees
// exactly what visitors see rather than their drafts.

export const POST_IMAGE_BUCKET = 'post-images';

// Card / list fields — everything but the body, which can be long.
const SUMMARY_COLUMNS =
  'id, title, slug, excerpt, cover_image_url, cover_image_alt, author, status, published_at, updated_at';
const FULL_COLUMNS = `${SUMMARY_COLUMNS}, body`;
const ADMIN_COLUMNS = `${FULL_COLUMNS}, created_at, created_by, updated_by`;

// --- Formatting -------------------------------------------------------------

// Dates render in one fixed zone: the build prerenders in UTC, and a visitor's
// own zone could put a late-evening post on a different day than the static
// HTML did, which breaks hydration. Qentoros is in Virginia.
const POST_TIME_ZONE = 'America/New_York';

export function formatPostDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: POST_TIME_ZONE,
  });
}

export function readingMinutes(body) {
  const words = markdownToPlainText(body).split(' ').filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

/** The post's excerpt, or the opening of its body when no excerpt was written. */
export function postSummary(post, max = 180) {
  if (post?.excerpt?.trim()) return post.excerpt.trim();
  const text = markdownToPlainText(post?.body);
  if (text.length <= max) return text;
  return `${text.slice(0, max).replace(/\s+\S*$/, '')}…`;
}

export function slugify(title) {
  return String(title || '')
    // Before normalizing: NFKD would expand ™ into the letters "TM".
    .replace(/[™®©]/g, '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '');
}

export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// --- Card balance -------------------------------------------------------------
//
// News cards sit side by side, so a post whose title or summary is far longer
// or shorter than its neighbours makes the row look lopsided. Publishing
// requires text inside these ranges; drafts can be anything. The card CSS also
// lines rows up and clamps (News.css), so this is about the words, not layout.
// The migration's check constraints are looser backstops — keep these inside them.
export const POST_LIMITS = {
  titleMax: 70,
  summaryMin: 70,
  summaryMax: 140,
};

/**
 * A summary drawn from the article: as many whole sentences as fit within the
 * card limit, else as many whole words with an ellipsis.
 */
export function summaryFromArticle(body, max = POST_LIMITS.summaryMax) {
  // Headings read badly run into prose ("Why it matters Septic joints…").
  const text = markdownToPlainText(String(body || '').replace(/^#{1,3}\s+.*$/gm, ''));
  if (text.length <= max) return text;
  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) || [];
  let out = '';
  for (const sentence of sentences) {
    if ((out + sentence).trim().length > max) break;
    out += sentence;
  }
  out = out.trim();
  if (out.length >= POST_LIMITS.summaryMin) return out;
  return `${text.slice(0, max - 1).replace(/\s+\S*$/, '').replace(/[,;:\s]+$/, '')}…`;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * What still stands between this post and publishing. Each item is
 * { key, label, ok, hint } so the editor can render a checklist.
 */
export function publishChecklist(post) {
  const title = (post.title || '').trim();
  const summary = (post.excerpt || '').trim();
  const words = markdownToPlainText(post.body).split(' ').filter(Boolean).length;
  const { titleMax, summaryMin, summaryMax } = POST_LIMITS;
  return [
    {
      key: 'title',
      label: 'Title',
      ok: title.length > 0 && title.length <= titleMax,
      hint: !title ? 'Add a title.' : `Keep it to ${titleMax} characters (now ${title.length}).`,
    },
    {
      key: 'summary',
      label: 'Summary',
      ok: summary.length >= summaryMin && summary.length <= summaryMax,
      hint: !summary
        ? `Write ${summaryMin}–${summaryMax} characters, or generate one from the article.`
        : summary.length < summaryMin
        ? `Too short — ${plural(summaryMin - summary.length, 'more character')} needed.`
        : `Too long — trim ${plural(summary.length - summaryMax, 'character')}.`,
    },
    {
      key: 'body',
      label: 'Article',
      ok: words > 0,
      hint: 'Write the article.',
    },
    {
      key: 'cover',
      label: 'Cover image',
      ok: Boolean(post.cover_image_url),
      hint: 'Add a cover image so every card has a photo.',
    },
  ];
}

/** 'draft' | 'scheduled' | 'published' — scheduled = published with a future date. */
export function postState(post, now = Date.now()) {
  if (post?.status !== 'published') return 'draft';
  if (post.published_at && new Date(post.published_at).getTime() > now) return 'scheduled';
  return 'published';
}

// --- Prerender snapshot -----------------------------------------------------
//
// The build fetches published posts and prerenders the homepage, /news and each
// /news/:slug with them (scripts/prerender-seo.mjs). The same data is embedded
// in that page as JSON so the client's first render matches the static markup
// exactly; the pages then refresh from Supabase after mount. The snapshot is
// stamped with the path it was rendered for, because the SPA fallback serves
// the homepage's HTML (and its snapshot) for every unprerendered route.

export const SNAPSHOT_ELEMENT_ID = 'news-snapshot';

let serverSnapshot = null;

/** Build-time only: the data render() should use for the route being rendered. */
export function setNewsSnapshot(snapshot) {
  serverSnapshot = snapshot;
}

let clientSnapshot;

function rawSnapshot() {
  if (typeof document === 'undefined') return serverSnapshot;
  if (clientSnapshot === undefined) {
    clientSnapshot = null;
    try {
      const el = document.getElementById(SNAPSHOT_ELEMENT_ID);
      if (el) clientSnapshot = JSON.parse(el.textContent);
    } catch {
      clientSnapshot = null;
    }
  }
  return clientSnapshot;
}

export function readNewsSnapshot(path) {
  const snapshot = rawSnapshot();
  return snapshot && snapshot.path === path ? snapshot : null;
}

/**
 * Whether the build saw any published post. Site-wide, so unlike the post
 * lists it holds on every page, including the SPA fallback; false when the page
 * wasn't prerendered (dev, or a build without news).
 */
export function readHasNewsHint() {
  return rawSnapshot()?.hasNews === true;
}

// --- "Is there any news?" ---------------------------------------------------
//
// The News nav/footer links and the homepage strip stay hidden until at least
// one post is live. One cheap query per page load, shared by every caller.

let hasNewsPromise = null;

/** Resolves true/false, or null when the check failed (keep the build's answer). */
export function fetchHasPublishedNews() {
  if (!hasNewsPromise) {
    // Runs inside the header on every marketing page, so any failure — even a
    // synchronous throw from a misconfigured client — must resolve, not reject.
    hasNewsPromise = (async () => {
      try {
        const { data, error } = await supabase
          .from('posts')
          .select('id')
          .eq('status', 'published')
          .lte('published_at', new Date().toISOString())
          .limit(1);
        return error ? null : data.length > 0;
      } catch {
        return null;
      }
    })();
  }
  return hasNewsPromise;
}

// Publishing, unpublishing or deleting changes the answer.
function resetHasNewsCache() {
  hasNewsPromise = null;
}

// --- Public reads -----------------------------------------------------------

export async function listPublishedPosts({ limit } = {}) {
  let query = supabase
    .from('posts')
    .select(SUMMARY_COLUMNS)
    .eq('status', 'published')
    .lte('published_at', new Date().toISOString())
    .order('published_at', { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error } = await query;
  return { posts: data || [], error };
}

export async function getPublishedPost(slug) {
  const { data, error } = await supabase
    .from('posts')
    .select(FULL_COLUMNS)
    .eq('slug', slug)
    .eq('status', 'published')
    .lte('published_at', new Date().toISOString())
    .maybeSingle();
  return { post: data, error };
}

// --- Admin ------------------------------------------------------------------

export async function listAllPosts() {
  const { data, error } = await supabase
    .from('posts')
    .select(ADMIN_COLUMNS)
    .order('updated_at', { ascending: false });
  return { posts: data || [], error };
}

export async function getPost(id) {
  const { data, error } = await supabase
    .from('posts')
    .select(ADMIN_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  return { post: data, error };
}

const EDITABLE_FIELDS = [
  'title',
  'slug',
  'excerpt',
  'body',
  'cover_image_url',
  'cover_image_alt',
  'author',
  'status',
  'published_at',
];

/**
 * Inserts (no id) or updates a post. Blank optional text fields are stored as
 * null so "no excerpt" and "no cover" are unambiguous.
 */
export async function savePost(fields, actorEmail) {
  const row = {};
  EDITABLE_FIELDS.forEach((key) => {
    if (!(key in fields)) return;
    const value = fields[key];
    row[key] = typeof value === 'string' && key !== 'body' ? value.trim() || null : value;
  });
  row.updated_by = actorEmail || null;

  const query = fields.id
    ? supabase.from('posts').update(row).eq('id', fields.id)
    : supabase.from('posts').insert({ ...row, created_by: actorEmail || null });
  const { data, error } = await query.select(ADMIN_COLUMNS).single();
  if (!error) resetHasNewsCache();
  return { post: data, error };
}

export async function deletePost(id) {
  const { error } = await supabase.from('posts').delete().eq('id', id);
  if (!error) resetHasNewsCache();
  return { error };
}

/** Uploads an image to the post-images bucket and returns its public URL. */
export async function uploadPostImage(file) {
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `${new Date().getFullYear()}/${id}.${ext}`;
  const { error } = await supabase.storage.from(POST_IMAGE_BUCKET).upload(path, file, {
    cacheControl: '31536000',
    contentType: file.type || undefined,
    upsert: false,
  });
  if (error) return { url: null, error };
  const { data } = supabase.storage.from(POST_IMAGE_BUCKET).getPublicUrl(path);
  return { url: data.publicUrl, error: null };
}
